import React from "react";
import { useState, useMemo, useEffect } from "react";
import {
  View,
  Image,
  StyleSheet,
  LayoutChangeEvent,
  Pressable,
} from "react-native";
import { NormalText } from "../../../../../common/native";
import { DraggableData, Rnd } from "react-rnd";
import { isLocalEnv } from "../../../../../../utils/functions";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import {
  getRotatedImageDimensions,
  rotatedCropToSourceCrop,
  sourceCropToRotatedCrop,
} from "./cropRotation";

type CropRotateImageProps = {
  imageUri: string;
  cropSize: { width: number; height: number };
  setCropSize: React.Dispatch<
    React.SetStateAction<{ width: number; height: number }>
  >;
  cropPosition: { x: number; y: number };
  setCropPosition: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }>
  >;
  originalImageDimensions?: { width: number; height: number };
  rotationDegrees: number;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  label: string;
};

const CropRotateImage: React.FC<CropRotateImageProps> = ({
  imageUri,
  cropSize,
  setCropSize,
  cropPosition,
  setCropPosition,
  originalImageDimensions,
  rotationDegrees,
  onRotateLeft,
  onRotateRight,
  label,
}) => {
  const effectiveImageDimensions = useMemo(() => {
    if (!originalImageDimensions) return undefined;

    return getRotatedImageDimensions(originalImageDimensions, rotationDegrees);
  }, [originalImageDimensions, rotationDegrees]);

  if (isLocalEnv) {
    if (imageUri.includes("RAW_BEFORE")) {
      console.log(
        "‼️‼️‼️ CropRotateImage.tsx: Before: cropSize",
        cropSize,
        "crop position",
        cropPosition,
      );
    }

    if (imageUri.includes("RAW_AFTER")) {
      console.log(
        "‼️‼️‼️ CropRotateImage.tsx: After: cropSize",
        cropSize,
        "crop position",
        cropPosition,
      );
    }
  }

  // State to track the container size
  const [containerSize, setContainerSize] = useState({
    width: 0,
    height: 0,
  });

  // Handle container layout to get its dimensions
  const handleContainerLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setContainerSize({ width, height });
  };

  // Calculate the scale factor and display offset based on contain mode
  const { scaleFactor, imageOffset } = useMemo(() => {
    if (
      !effectiveImageDimensions ||
      !containerSize.width ||
      !containerSize.height
    ) {
      return {
        scaleFactor: { x: 1, y: 1 },
        imageOffset: { x: 0, y: 0 },
      };
    }

    // Calculate the aspect ratios
    const containerAspect = containerSize.width / containerSize.height;
    const imageAspect =
      effectiveImageDimensions.width / effectiveImageDimensions.height;

    let displayWidth,
      displayHeight,
      offsetX = 0,
      offsetY = 0;

    if (imageAspect > containerAspect) {
      // Image is wider than container (letterboxing - black bars on top/bottom)
      displayWidth = containerSize.width;
      displayHeight = containerSize.width / imageAspect;
      offsetY = (containerSize.height - displayHeight) / 2;
    } else {
      // Image is taller than container (pillarboxing - black bars on left/right)
      displayHeight = containerSize.height;
      displayWidth = containerSize.height * imageAspect;
      offsetX = (containerSize.width - displayWidth) / 2;
    }

    if (isLocalEnv) {
      console.log("Calculated display size:", displayWidth, displayHeight);
      console.log("Image offset:", offsetX, offsetY);
    }

    return {
      scaleFactor: {
        x: displayWidth / effectiveImageDimensions.width,
        y: displayHeight / effectiveImageDimensions.height,
      },
      imageOffset: {
        x: offsetX,
        y: offsetY,
      },
    };
  }, [effectiveImageDimensions, containerSize]);

  // Log scale factor and offset
  useEffect(() => {
    console.log("Scale factor:", scaleFactor);
    console.log("Image offset:", imageOffset);
  }, [scaleFactor, imageOffset]);

  const sourceImageDimensions = useMemo(
    () => originalImageDimensions ?? { width: 1, height: 1 },
    [originalImageDimensions],
  );
  const rotatedDisplayCrop = useMemo(
    () =>
      sourceCropToRotatedCrop(
        {
          x: cropPosition.x,
          y: cropPosition.y,
          width: cropSize.width,
          height: cropSize.height,
        },
        sourceImageDimensions,
        rotationDegrees,
      ),
    [
      cropPosition.x,
      cropPosition.y,
      cropSize.width,
      cropSize.height,
      sourceImageDimensions,
      rotationDegrees,
    ],
  );

  const handleDragStop = React.useCallback(
    (e: any, d: DraggableData) => {
      // Convert display coordinates to original image coordinates
      // We need to subtract the offset before scaling
      const rotatedCrop = {
        x: (d.x - imageOffset.x) / scaleFactor.x,
        y: (d.y - imageOffset.y) / scaleFactor.y,
        width: rotatedDisplayCrop.width,
        height: rotatedDisplayCrop.height,
      };
      const sourceCrop = rotatedCropToSourceCrop(
        rotatedCrop,
        sourceImageDimensions,
        rotationDegrees,
      );

      console.log("Drag stopped:", {
        displayX: d.x,
        displayY: d.y,
        adjustedX: d.x - imageOffset.x,
        adjustedY: d.y - imageOffset.y,
        convertedX: sourceCrop.x,
        convertedY: sourceCrop.y,
      });

      setCropPosition({
        x: sourceCrop.x,
        y: sourceCrop.y,
      });
    },
    [
      setCropPosition,
      scaleFactor,
      imageOffset,
      rotatedDisplayCrop,
      sourceImageDimensions,
      rotationDegrees,
    ],
  );

  const handleResizeStop = React.useCallback(
    (
      e: any,
      direction: any,
      ref: { offsetWidth: any; offsetHeight: any },
      delta: any,
      position: { x: any; y: any },
    ) => {
      // Convert display dimensions to original image dimensions
      const rotatedCrop = {
        x: (position.x - imageOffset.x) / scaleFactor.x,
        y: (position.y - imageOffset.y) / scaleFactor.y,
        width: ref.offsetWidth / scaleFactor.x,
        height: ref.offsetHeight / scaleFactor.y,
      };
      const sourceCrop = rotatedCropToSourceCrop(
        rotatedCrop,
        sourceImageDimensions,
        rotationDegrees,
      );

      console.log("Resize stopped:", {
        displayWidth: ref.offsetWidth,
        displayHeight: ref.offsetHeight,
        convertedWidth: sourceCrop.width,
        convertedHeight: sourceCrop.height,
        displayX: position.x,
        displayY: position.y,
        convertedX: sourceCrop.x,
        convertedY: sourceCrop.y,
      });

      setCropSize({
        width: sourceCrop.width,
        height: sourceCrop.height,
      });

      setCropPosition({
        x: sourceCrop.x,
        y: sourceCrop.y,
      });
    },
    [
      setCropSize,
      setCropPosition,
      scaleFactor,
      imageOffset,
      sourceImageDimensions,
      rotationDegrees,
    ],
  );

  // Convert rotated image coordinates to display coordinates
  const displaySize = {
    width: rotatedDisplayCrop.width * scaleFactor.x,
    height: rotatedDisplayCrop.height * scaleFactor.y,
  };

  const displayPosition = {
    x: rotatedDisplayCrop.x * scaleFactor.x + imageOffset.x,
    y: rotatedDisplayCrop.y * scaleFactor.y + imageOffset.y,
  };

  return (
    <View>
      <View style={styles.container}>
        <View style={styles.toolbar}>
          <NormalText text="Crop" style={styles.text} />
          <View style={styles.rotateControls}>
            <Pressable
              onPress={onRotateLeft}
              style={styles.iconButton}
              accessibilityRole="button"
              accessibilityLabel={`Rotate ${label} image counterclockwise`}
            >
              <MaterialIcons name="rotate-left" size={20} color="#282B33" />
            </Pressable>
            <Pressable
              onPress={onRotateRight}
              style={styles.iconButton}
              accessibilityRole="button"
              accessibilityLabel={`Rotate ${label} image clockwise`}
            >
              <MaterialIcons name="rotate-right" size={20} color="#282B33" />
            </Pressable>
          </View>
        </View>
        {/* Debugging info in local*/}
        {isLocalEnv && (
          <View style={styles.debugInfo}>
            <NormalText
              text={`Original: ${Math.round(cropSize.width)}×${Math.round(cropSize.height)}`}
              style={styles.debugText}
            />
            <NormalText
              text={`Display: ${Math.round(displaySize.width)}×${Math.round(displaySize.height)}`}
              style={styles.debugText}
            />
            <NormalText
              text={`Scale: ${scaleFactor.x.toFixed(2)}×${scaleFactor.y.toFixed(2)}`}
              style={styles.debugText}
            />
            <NormalText
              text={`Offset: ${Math.round(imageOffset.x)}×${Math.round(imageOffset.y)}`}
              style={styles.debugText}
            />
          </View>
        )}
        <View style={styles.imageWrapper} onLayout={handleContainerLayout}>
          <Image
            source={{ uri: imageUri }}
            style={[
              styles.image,
              { transform: [{ rotate: `${rotationDegrees}deg` }] },
            ]}
            resizeMode="contain"
          />
          <Rnd
            bounds="parent"
            minHeight={50}
            minWidth={50}
            size={displaySize}
            position={displayPosition}
            style={{
              borderWidth: 1,
              borderColor: "#fff",
              borderStyle: "solid",
            }}
            onDragStop={handleDragStop}
            onResizeStop={handleResizeStop}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    borderRadius: 8,
    gap: 4,
    padding: 3,
    aspectRatio: 1,
    width: "100%",
  },
  text: {
    textAlign: "center",
  },
  toolbar: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  rotateControls: {
    flexDirection: "row",
    gap: 4,
  },
  iconButton: {
    width: 44,
    height: 44,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#282B33",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  imageWrapper: {
    flex: 1,
    width: "100%",
    backgroundColor: "black",
    position: "relative",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  debugInfo: {
    padding: 4,
    backgroundColor: "rgba(0,0,0,0.7)",
  },
  debugText: {
    color: "white",
    fontSize: 10,
  },
});

export default React.memo(CropRotateImage);
